import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { generateMobileTimeSlots } from '../../../../utils/calendar/mobile/mobileTimeFormatting';

const MobileTimePicker = ({
                              isOpen,
                              onClose,
                              onConfirm,
                              initialStartTime,
                              initialEndTime,
                              minTime,
                              maxTime,
                              snapInterval = 15,
                              minDuration = 15,
                              maxDuration = 480, // 8 hours
                              showDuration = true,
                              title = "Wybierz godziny"
                          }) => {
    const [startTime, setStartTime] = useState(initialStartTime || dayjs().hour(9).minute(0));
    const [endTime, setEndTime] = useState(initialEndTime || dayjs().hour(10).minute(0));
    const [activeTab, setActiveTab] = useState('start'); // 'start' | 'end'

    const startPickerRef = useRef(null);
    const endPickerRef = useRef(null);

    // Reset times when modal opens
    useEffect(() => {
        if (isOpen) {
            setStartTime(initialStartTime || dayjs().hour(9).minute(0));
            setEndTime(initialEndTime || dayjs().hour(10).minute(0));
            setActiveTab('start');
        }
    }, [isOpen, initialStartTime, initialEndTime]);

    // Generate time slots
    const timeSlots = generateMobileTimeSlots(
        minTime || '06:00',
        maxTime || '22:00',
        snapInterval
    );

    // Calculate duration
    const duration = endTime.diff(startTime, 'minute');
    const durationHours = Math.floor(duration / 60);
    const durationMinutes = duration % 60;

    // Handle time selection
    const handleTimeSelect = (time, isStart) => {
        const selectedTime = dayjs().hour(time.hour).minute(time.minute);

        if (isStart) {
            setStartTime(selectedTime);
            // Adjust end time if needed
            const minEndTime = selectedTime.add(minDuration, 'minute');
            if (endTime.isBefore(minEndTime)) {
                setEndTime(minEndTime);
            }
            // Auto-switch to end time selection
            setTimeout(() => setActiveTab('end'), 300);
        } else {
            // Ensure end time is after start time
            const minEndTime = startTime.add(minDuration, 'minute');
            if (selectedTime.isBefore(minEndTime)) {
                setEndTime(minEndTime);
            } else {
                setEndTime(selectedTime);
            }
        }
    };

    // Quick duration buttons
    const quickDurations = [30, 60, 90, 120];

    const handleQuickDuration = (minutes) => {
        const newEndTime = startTime.add(minutes, 'minute');
        const maxEndTime = dayjs().hour(22).minute(0);

        if (newEndTime.isAfter(maxEndTime)) {
            setEndTime(maxEndTime);
        } else {
            setEndTime(newEndTime);
        }
    };

    const handleConfirm = () => {
        onConfirm(startTime, endTime);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal-content mobile-time-picker-modal"
                onClick={(e) => e.stopPropagation()}
                style={{
                    maxWidth: '400px',
                    width: '90%',
                    maxHeight: '80vh',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <div className="modal-header">
                    <h3 className="modal-title">{title}</h3>
                    <button className="close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="modal-body" style={{ flex: 1, overflow: 'hidden', padding: 0 }}>
                    {/* Tab selector */}
                    <div className="mobile-time-picker-tabs" style={{
                        display: 'flex',
                        borderBottom: '1px solid #e5e7eb',
                        background: '#f9fafb'
                    }}>
                        <button
                            className={`mobile-tab ${activeTab === 'start' ? 'active' : ''}`}
                            onClick={() => setActiveTab('start')}
                            style={{
                                flex: 1,
                                padding: '12px',
                                border: 'none',
                                background: activeTab === 'start' ? '#fff' : 'transparent',
                                borderBottom: activeTab === 'start' ? '2px solid #3b82f6' : 'none',
                                fontWeight: activeTab === 'start' ? '600' : '400'
                            }}
                        >
                            <div>Początek</div>
                            <div style={{ fontSize: '18px', marginTop: '4px' }}>
                                {startTime.format('HH:mm')}
                            </div>
                        </button>
                        <button
                            className={`mobile-tab ${activeTab === 'end' ? 'active' : ''}`}
                            onClick={() => setActiveTab('end')}
                            style={{
                                flex: 1,
                                padding: '12px',
                                border: 'none',
                                background: activeTab === 'end' ? '#fff' : 'transparent',
                                borderBottom: activeTab === 'end' ? '2px solid #3b82f6' : 'none',
                                fontWeight: activeTab === 'end' ? '600' : '400'
                            }}
                        >
                            <div>Koniec</div>
                            <div style={{ fontSize: '18px', marginTop: '4px' }}>
                                {endTime.format('HH:mm')}
                            </div>
                        </button>
                    </div>

                    {/* Duration display */}
                    {showDuration && (
                        <div className="mobile-duration-display" style={{
                            padding: '16px',
                            background: '#f3f4f6',
                            textAlign: 'center',
                            borderBottom: '1px solid #e5e7eb'
                        }}>
                            <div style={{ fontSize: '14px', color: '#6b7280' }}>Czas trwania</div>
                            <div style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginTop: '4px' }}>
                                {durationHours > 0 && `${durationHours} godz `}
                                {durationMinutes > 0 && `${durationMinutes} min`}
                            </div>
                        </div>
                    )}

                    {/* Quick duration buttons (only for end time) */}
                    {activeTab === 'end' && (
                        <div className="mobile-quick-durations" style={{
                            display: 'flex',
                            gap: '8px',
                            padding: '16px',
                            borderBottom: '1px solid #e5e7eb'
                        }}>
                            {quickDurations.map(minutes => (
                                <button
                                    key={minutes}
                                    onClick={() => handleQuickDuration(minutes)}
                                    className="mobile-quick-duration-btn"
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        background: duration === minutes ? '#3b82f6' : '#fff',
                                        color: duration === minutes ? '#fff' : '#374151',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                >
                                    {minutes < 60 ? `${minutes}m` : `${minutes/60}h`}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Time slots */}
                    <div
                        className="mobile-time-slots-picker"
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '16px'
                        }}
                    >
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: '8px'
                        }}>
                            {timeSlots.map((slot) => {
                                const slotTime = dayjs().hour(slot.hour).minute(slot.minute);
                                const isSelected = activeTab === 'start'
                                    ? slotTime.isSame(startTime, 'minute')
                                    : slotTime.isSame(endTime, 'minute');

                                const isDisabled = activeTab === 'end'
                                    ? slotTime.isSameOrBefore(startTime.add(minDuration - snapInterval, 'minute'))
                                    : false;

                                return (
                                    <button
                                        key={slot.value}
                                        ref={isSelected ? (activeTab === 'start' ? startPickerRef : endPickerRef) : null}
                                        onClick={() => !isDisabled && handleTimeSelect(slot, activeTab === 'start')}
                                        disabled={isDisabled}
                                        className={`mobile-time-slot-btn ${isSelected ? 'selected' : ''}`}
                                        style={{
                                            padding: '12px 8px',
                                            border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            background: isSelected ? '#3b82f6' : '#fff',
                                            color: isSelected ? '#fff' : isDisabled ? '#d1d5db' : '#374151',
                                            fontSize: '16px',
                                            fontWeight: isSelected ? '600' : '400',
                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                            opacity: isDisabled ? 0.5 : 1
                                        }}
                                    >
                                        {slot.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="modal-footer" style={{
                    display: 'flex',
                    gap: '12px',
                    padding: '16px',
                    borderTop: '1px solid #e5e7eb'
                }}>
                    <button
                        className="cancel-btn"
                        onClick={onClose}
                        style={{
                            flex: 1,
                            padding: '12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            background: '#fff',
                            fontSize: '16px',
                            fontWeight: '500'
                        }}
                    >
                        Anuluj
                    </button>
                    <button
                        className="confirm-btn"
                        onClick={handleConfirm}
                        disabled={duration < minDuration || duration > maxDuration}
                        style={{
                            flex: 1,
                            padding: '12px',
                            border: 'none',
                            borderRadius: '8px',
                            background: '#3b82f6',
                            color: '#fff',
                            fontSize: '16px',
                            fontWeight: '500',
                            opacity: (duration < minDuration || duration > maxDuration) ? 0.5 : 1,
                            cursor: (duration < minDuration || duration > maxDuration) ? 'not-allowed' : 'pointer'
                        }}
                    >
                        Potwierdź
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MobileTimePicker;